import {
  GraphComponent,
  GroupLayeringPolicy,
  HierarchicalLayout,
  HierarchicalLayoutData,
  HierarchicalLayoutLayeringStrategy,
  HierarchicalLayoutNodeDescriptor,
  IColumn,
  IGraph,
  INode,
  IRectangle,
  ITable,
  LayoutExecutor,
  LayoutOrientation,
  MirroredOrientations,
  OrientationStage,
  Rect,
  RecursiveGroupLayout,
  RoutingStyleDescriptor,
  Table
} from '@yfiles/yfiles';
import {
  constrainedSequenceForProducts,
  constrainedSequenceForProductsByRevenue
} from './defaults.ts';
import { edgeRoutingStyle, getEdgeThickness } from './styles.ts';
import ValueStreamLayout from './ValueStreamLayout';

export async function runLayout(
  graphComponent: GraphComponent,
  stages: string[],
  incremental = false,
  mode: 'TABULAR' | 'FLOW' = 'FLOW'
) {
  console.debug(
    'Running layout. Stages: ' + stages + ', incremental: ' + incremental + ', mode: ' + mode
  );
  const hierarchicalLayout = new HierarchicalLayout();

  hierarchicalLayout.defaultEdgeDescriptor.routingStyleDescriptor = new RoutingStyleDescriptor(
    edgeRoutingStyle
  );
  hierarchicalLayout.defaultEdgeDescriptor.minimumDistance = 5;

  hierarchicalLayout.defaultEdgeDescriptor.minimumFirstSegmentLength = 50;
  hierarchicalLayout.defaultEdgeDescriptor.minimumLastSegmentLength = 50;

  hierarchicalLayout.edgeDistance = 5;

  hierarchicalLayout.layoutOrientation = LayoutOrientation.LEFT_TO_RIGHT;
  hierarchicalLayout.coordinateAssigner.groupCompaction = true;
  hierarchicalLayout.fromScratchLayeringStrategy =
    HierarchicalLayoutLayeringStrategy.HIERARCHICAL_TOPMOST;

  if (mode === 'TABULAR') {
    hierarchicalLayout.coordinateAssigner.symmetryOptimizationStrategy = 'none';
    hierarchicalLayout.layoutStages.get(OrientationStage)!.mirroredOrientations =
      MirroredOrientations.LEFT_TO_RIGHT;
    hierarchicalLayout.groupLayeringPolicy = GroupLayeringPolicy.IGNORE_GROUPS;
  }

  const layout = new ValueStreamLayout(hierarchicalLayout);

  const layoutData = new HierarchicalLayoutData({
    edgeThickness: getEdgeThickness
  });

  layoutData.ports.sourcePortAlignmentIds.mapperFunction = (edge) => edge.tag.product;
  layoutData.ports.targetPortAlignmentIds.mapperFunction = (edge) => edge.tag.product;

  const graph = graphComponent.graph;

  const portSpacing = 5;

  layoutData.nodeDescriptors.mapperFunction = (node) => {
    const groupedByKey = graph.inEdgesAt(node).reduce((acc: { [index: string]: number }, edge) => {
      const product: string = edge.tag.product;
      const thickness = getEdgeThickness(edge);
      if (!acc[product]) {
        acc[product] = thickness;
      } else {
        acc[product] = Math.max(acc[product], thickness);
      }
      return acc;
    }, {});

    let totalThickness = 0;
    for (const product of Object.keys(groupedByKey)) {
      totalThickness += groupedByKey[product];
    }

    const productCount = Object.keys(groupedByKey).length;
    const height = node.layout.height;
    const borderSpace = 0.5 * (height - totalThickness - (productCount - 1) * portSpacing);
    // The border space calculated above may be negative. Then we cannot keep the desired port spacing.
    const ratio = Math.max(borderSpace / portSpacing, 0);
    return new HierarchicalLayoutNodeDescriptor({ borderToPortGapRatio: ratio });
  };

  const nodeIds = new Map<INode, number>();
  graph.nodes.forEach((node, i) => nodeIds.set(node, i));

  const tableNode = graph.getChildren(null).first();
  const tableModel = ITable.getTable(tableNode!) as Table;
  const tableLayout = tableNode!.layout;
  const columns = tableModel.columns;
  const firstColumn = tableModel.columns.first();

  // configure sequence constraints for product start nodes
  const sequenceConstraints = layoutData.sequenceConstraints;
  graph.nodes
    .filter((node) => node.tag && node.tag.productStart)
    .forEach((node) => {
      if (constrainedSequenceForProducts) {
        if (constrainedSequenceForProductsByRevenue) {
          sequenceConstraints.itemComparables.mapper.set(node, node.tag.revenue);
        } else {
          sequenceConstraints.itemComparables.mapper.set(node, graph.outDegree(node));
        }
      }
      // Ensure product nodes are placed in the first column (Products stage)
      ensureNodeInColumn(tableLayout, columns.at(0)!, node, graph);
    });

  // fix positions for nodes outside their columns
  graph.nodes
    .filter((node) => node.tag && node.tag.stage && !node.tag.productStart)
    .forEach((node) => {
      const index = stages.indexOf(node.tag.stage);
      if (index >= 0) {
        // Find the column for this stage - columns are created in the same order as stages
        const column = columns.at(index)!;
        ensureNodeInColumn(tableLayout, column, node, graph);
      }
    });

  hierarchicalLayout.layoutStages.addPostprocessor(new RecursiveGroupLayout());

  const layoutExecutor = new LayoutExecutor({
    graphComponent,
    layout,
    layoutData,
    updateContentBounds: true,
    animateViewport: !incremental,
    nodeComparator: (x, y) => x.tag.nodeId - y.tag.nodeId,
    edgeComparator: (x, y) => x.tag.edgeId - y.tag.edgeId
  });
  layoutExecutor.tableLayoutConfigurator.compaction = true;
  return layoutExecutor.start();
}

function ensureNodeInColumn(tableLayout: IRectangle, column: IColumn, node: INode, graph: IGraph) {
  if (!column) {
    return;
  }
  const columnLayout = new Rect(
    tableLayout.x + column.layout.x,
    tableLayout.y + column.layout.y,
    column.layout.width,
    column.layout.height
  );
  graph.setNodeCenter(node, columnLayout.center);
}
